import React from 'react';
import { Link } from 'react-router-dom';
import type { SharedMemberItem } from '../../api/sharing';
import { avatarGradient } from '../member/memberAvatar';
import { memberDisplayInitials } from '../../utils/memberDisplay';
import { formatDocumentsCountRo } from '../../utils/dates';

interface SharedMembersPanelProps {
  members: SharedMemberItem[];
}

const IconArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SharedMembersPanel: React.FC<SharedMembersPanelProps> = ({ members }) => {
  return (
    <section className="dash-panel dash-panel--shared" aria-labelledby="dash-shared-title">
      <div className="dash-panel-head">
        <h2 className="dash-panel-title" id="dash-shared-title">
          Partajate cu mine
        </h2>
        {members.length > 0 && (
          <Link to="/shared-members" className="dash-panel-link">
            Toate <IconArrow />
          </Link>
        )}
      </div>
      {members.length === 0 ? (
        <div className="dash-panel-empty">
          <p>Nu există invitații active.</p>
          <p className="dash-panel-empty-hint">
            Când cineva îți partajează un dosar medical, îl vei vedea aici.
          </p>
        </div>
      ) : (
        <ul className="dash-panel-list">
          {members.map((m) => {
            const docs = m.stats?.document_count ?? 0;
            const pending = m.stats?.pending_documents ?? 0;
            const initials = memberDisplayInitials({ name: m.name });
            const gradient = avatarGradient(m.member_id);
            return (
              <li key={m.member_id}>
                <Link to={`/members/${m.member_id}`} className="dash-fam-row">
                  <span
                    className="dash-shared-avatar"
                    style={{ background: gradient }}
                    aria-hidden
                  >
                    {initials}
                  </span>
                  <div className="dash-fam-row-main">
                    <div className="dash-fam-row-title">
                      <span className="dash-fam-row-name">{m.name}</span>
                    </div>
                    <div className="dash-fam-row-meta">
                      {m.family_name && (
                        <span className="dash-fam-row-meta-item">{m.family_name}</span>
                      )}
                      <span className="dash-fam-row-meta-item">
                        {formatDocumentsCountRo(docs)}
                      </span>
                      {pending > 0 && (
                        <span
                          className="dash-fam-row-meta-item dash-fam-row-meta-item--pending"
                          role="status"
                        >
                          {pending} în procesare
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="dash-fam-row-cta" aria-hidden>
                    Deschide <IconArrow />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SharedMembersPanel;
